export interface WorkEnvironmentNode {
  id: string;
  name: string;
  description: string;
  pros: string[];
  cons: string[];
  suitableBusinessTypes?: string[];
  technicalRequirements?: string[];
}

export const WORK_ENVIRONMENT_TAXONOMY: WorkEnvironmentNode[] = [
  {
    id: 'home-office',
    name: 'Home Office',
    description: 'Working primarily from a dedicated space in your home',
    pros: [
      'Zero commute time',
      'Flexible schedule',
      'Low overhead costs',
      'Comfortable environment',
      'Potential tax deductions'
    ],
    cons: [
      'Potential isolation',
      'Requires self-discipline',
      'Limited networking',
      'Possible distractions',
      'Need for dedicated workspace'
    ],
    suitableBusinessTypes: [
      'Freelancing',
      'Consulting',
      'Digital Services',
      'Online Coaching',
      'Content Creation'
    ],
    technicalRequirements: [
      'Reliable high-speed internet',
      'Ergonomic workspace',
      'Professional video conferencing setup',
      'Cybersecurity measures'
    ]
  },
  {
    id: 'co-working-space',
    name: 'Co-working Space',
    description: 'Shared professional workspace with other entrepreneurs and remote workers',
    pros: [
      'Networking opportunities',
      'Professional environment',
      'Flexible membership',
      'Access to meeting rooms',
      'Community support'
    ],
    cons: [
      'Monthly membership costs',
      'Potential noise distractions',
      'Less privacy',
      'Commute required',
      'Varying workspace quality'
    ],
    suitableBusinessTypes: [
      'Startups',
      'Freelance Teams',
      'Small Consultancies',
      'Creative Professionals'
    ],
    technicalRequirements: [
      'High-speed shared internet',
      'Power outlets',
      'Meeting room access',
      'Print and scanning facilities'
    ]
  },
  {
    id: 'hybrid-remote',
    name: 'Hybrid Remote',
    description: 'Combination of home office and occasional in-person collaboration',
    pros: [
      'Flexibility',
      'Balance between isolation and interaction',
      'Reduced commute',
      'Adaptable work style',
      'Cost-effective'
    ],
    cons: [
      'Requires careful scheduling',
      'Potential communication challenges',
      'Technology integration complexity',
      'Maintaining team cohesion'
    ],
    suitableBusinessTypes: [
      'Tech Startups',
      'Digital Agencies',
      'Software Development',
      'Marketing Teams'
    ],
    technicalRequirements: [
      'Cloud collaboration tools',
      'Video conferencing software',
      'Project management platforms',
      'Secure VPN access'
    ]
  },
  {
    id: 'dedicated-office',
    name: 'Dedicated Private Office',
    description: 'Rented or owned private workspace exclusively for your business',
    pros: [
      'Professional image',
      'Complete control of environment',
      'Space for team expansion',
      'Separation of work and home',
      'Client meeting capabilities'
    ],
    cons: [
      'High overhead costs',
      'Long-term commitment',
      'Maintenance responsibilities',
      'Fixed location',
      'Potential underutilization'
    ],
    suitableBusinessTypes: [
      'Established Businesses',
      'Consulting Firms',
      'Professional Services',
      'Manufacturing Startups'
    ],
    technicalRequirements: [
      'Professional networking infrastructure',
      'Dedicated server room',
      'Advanced security systems',
      'Scalable workspace design'
    ]
  },
  {
    id: 'mobile-nomad',
    name: 'Digital Nomad / Location Independent',
    description: 'Working from various locations, leveraging global connectivity',
    pros: [
      'Ultimate flexibility',
      'Travel opportunities',
      'Diverse work environments',
      'Cultural experiences',
      'Global networking'
    ],
    cons: [
      'Inconsistent work environment',
      'Connectivity challenges',
      'Time zone complications',
      'Higher living expenses',
      'Visa and legal complexities'
    ],
    suitableBusinessTypes: [
      'Freelance Services',
      'Online Businesses',
      'Digital Marketing',
      'Content Creation',
      'Consulting'
    ],
    technicalRequirements: [
      'High-performance laptop',
      'Multiple mobile internet options',
      'Cloud-based tools',
      'International payment systems',
      'Robust cybersecurity'
    ]
  }
];

// Utility function to find work environment by ID
export function findWorkEnvironmentById(id: string): WorkEnvironmentNode | undefined {
  return WORK_ENVIRONMENT_TAXONOMY.find(env => env.id === id);
}
